import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, signal } from '@angular/core';
import { ButtonV3Component, MultiSelectV3Component, SelectOption, SelectV2Module, SnackbarV3Service } from '@gea/digital-ui-lib';
import { Divider } from 'primeng/divider';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { UtilityService } from '../../../../shared/services/utility.service';
import { UserManagementService } from '../../../../services/user-management.service';
import { Target, TargetDTO } from '../../user-management.model';
import { catchError, EMPTY, finalize, Subject, switchMap } from 'rxjs';
import { StyleClass } from 'primeng/styleclass';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-add-grants',
  imports: [ButtonV3Component, Divider, FormsModule, ReactiveFormsModule, SelectV2Module, MultiSelectV3Component, StyleClass],
  templateUrl: './add-grants.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddGrantsComponent implements OnInit {
  grantOptions: SelectOption[] = [];
  targets: SelectOption[] = [];
  addGrantsForm = this.formBuilder.group({
    grant: this.formBuilder.control<string | number | null>(null, Validators.required),
    target: this.formBuilder.nonNullable.control<SelectOption[]>([], Validators.required),
  });
  userId = '';
  isTargetLoading = signal(false);
  addingGrants = signal(false);
  private grantSelect$ = new Subject<string>();

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly dialogRef: DynamicDialogRef,
    private readonly dialogConfig: DynamicDialogConfig,
    private readonly utilityService: UtilityService,
    private readonly userManagementService: UserManagementService,
    private readonly snackbarService: SnackbarV3Service,
    private readonly destroyed: DestroyRef
  ) {}

  ngOnInit(): void {
    this.grantOptions = this.utilityService.constructSelectOptions(this.dialogConfig.data?.grants ?? []);
    this.userId = this.dialogConfig.data?.userId || '';
    this.listenToGrantChanges();
  }

  private listenToGrantChanges(): void {
    this.grantSelect$
      .pipe(
        switchMap((grant) => {
          this.isTargetLoading.set(true);
          this.targets = [];
          this.addGrantsForm.controls.target.reset([]);
          return this.userManagementService.getTargetsForGrant(grant, this.userId).pipe(
            catchError(() => EMPTY),
            finalize(() => this.isTargetLoading.set(false))
          );
        }),
        takeUntilDestroyed(this.destroyed)
      )
      .subscribe({
        next: (response: TargetDTO) => {
          this.targets = this.constructSelectOptions(response.targetEntities);
        },
      });
  }

  private resolveGrantOption(selection: SelectOption | string | number | null | undefined): SelectOption | undefined {
    if (selection !== null && typeof selection === 'object') {
      return selection;
    }
    return this.grantOptions.find((option) => option.value === selection);
  }

  protected selectChanged(selection: SelectOption | string | number) {
    const selectedGrant = this.resolveGrantOption(selection);
    if (!selectedGrant?.name) return;

    this.addGrantsForm.patchValue({ grant: selectedGrant.value });
    this.grantSelect$.next(selectedGrant.name);
  }

  protected addGrantsToUser() {
    if (this.addingGrants() || !this.addGrantsForm.valid) return;

    const grant = this.resolveGrantOption(this.addGrantsForm.controls.grant.value);
    if (!grant) return;

    this.addingGrants.set(true);
    this.userManagementService
      .setGrantToUser(grant, this.addGrantsForm.controls.target.value, this.userId)
      .pipe(
        finalize(() => this.addingGrants.set(false)),
        takeUntilDestroyed(this.destroyed)
      )
      .subscribe({
        next: () => {
          this.snackbarService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Grant has been successfully added to the user.',
          });
          this.dialogRef.close(true);
        },
      });
  }

  constructSelectOptions(options: Target[]): SelectOption[] {
    return options.map((target) => ({ value: target.id, name: target.name }));
  }

  close(): void {
    this.dialogRef.close();
  }
}
